import React, { useEffect, useRef, useState } from 'react';
import { FaCamera, FaTimes } from 'react-icons/fa';

const QRCODE_DECODER_CDN = 'https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.js';

export default function QRScanner({ onScan, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const scriptLoaded = useRef(false);
  const scanFrameRef = useRef(null);
  const scanHandledRef = useRef(false);

  useEffect(() => {
    return () => {
      if (scanFrameRef.current) {
        cancelAnimationFrame(scanFrameRef.current);
      }
      stopScanning();
    };
  }, []);

  const stopScanning = () => {
    if (scanFrameRef.current) {
      cancelAnimationFrame(scanFrameRef.current);
      scanFrameRef.current = null;
    }
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setScanning(false);
  };

  const scanQRCode = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video || !window.jsQR || scanHandledRef.current) return;

    const context = canvas.getContext('2d', { willReadFrequently: true });
    const drawFrame = () => {
      if (!video.videoWidth || !video.videoHeight || scanHandledRef.current) return;

      canvas.width = Math.max(video.videoWidth, 320);
      canvas.height = Math.max(video.videoHeight, 320);
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const code = window.jsQR(imageData.data, canvas.width, canvas.height);

      if (code?.data) {
        scanHandledRef.current = true;
        onScan(code.data);
        stopScanning();
        return;
      }

      scanFrameRef.current = requestAnimationFrame(drawFrame);
    };

    drawFrame();
  };

  const startScanning = async () => {
    try {
      setError(null);
      scanHandledRef.current = false;
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setScanning(true);

        if (!window.jsQR && !scriptLoaded.current) {
          scriptLoaded.current = true;
          const script = document.createElement('script');
          script.src = QRCODE_DECODER_CDN;
          script.onload = () => { scanQRCode(); };
          document.body.appendChild(script);
        } else if (window.jsQR) {
          scanQRCode();
        }
      }
    } catch (err) {
      setError('Unable to access camera. Please check permissions and try again.');
      console.error('Camera error:', err);
    }
  };

  const handleClose = () => {
    stopScanning();
    onClose();
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.9)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
      <div style={{ position: 'relative', width: '90%', maxWidth: '500px', aspect: '1 / 1', background: '#000', borderRadius: '8px', overflow: 'hidden', border: '3px solid #0052cc' }}>
        <video ref={videoRef} autoPlay playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        <canvas ref={canvasRef} style={{ display: 'none' }} width={250} height={250} />
      </div>

      <div style={{ marginTop: '2rem', textAlign: 'center', color: 'white' }}>
        {!scanning ? (
          <button onClick={startScanning} style={{ padding: '1rem 2rem', background: '#0052cc', color: 'white', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0 auto' }}>
            <FaCamera /> Start Scanning
          </button>
        ) : (
          <p style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>Position the QR code in the frame and hold steady...</p>
        )}

        {error && (<p style={{ color: '#d32f2f', marginTop: '1rem' }}>{error}</p>)}
      </div>

      <button onClick={handleClose} style={{ position: 'absolute', top: '1rem', right: '1rem', background: '#d32f2f', color: 'white', border: 'none', borderRadius: '50%', width: '50px', height: '50px', fontSize: '1.5rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <FaTimes />
      </button>
    </div>
  );
}
